import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { SocketService } from '../../../../../services/socket.service';
import { AuthService } from '../../../../auth/services/auth.service';
import { RolesEnum } from '../../../../shared/enums/roles.enum';
import { TenantContextService } from '../../../../shared/services/tenant-context.service';
import { ChatActions, ChatEvents } from '../../../modules/chat/enums/chat.enum';
import { CrmChatsService } from './crm-chats.service';

const CRM_CHAT_ROLES = new Set<RolesEnum>([
  RolesEnum.ADMIN,
  RolesEnum.MANAGER,
  RolesEnum.JUN_MANAGER,
  RolesEnum.AGENT,
  RolesEnum.SUPER_ADMIN,
]);

@Injectable({ providedIn: 'root' })
export class CrmChatInboxStateService {
  private readonly invalidationSubject = new Subject<void>();
  private readonly unreadChatsSubject = new BehaviorSubject(0);
  private contextEnabled = false;

  readonly changes$ = this.invalidationSubject.asObservable();
  readonly unreadChats$ = this.unreadChatsSubject.asObservable().pipe(distinctUntilChanged());

  get unreadChats(): number {
    return this.unreadChatsSubject.value;
  }

  private readonly handleConnect = (): void => this.subscribeToTenantRoom();
  private readonly handleInvalidation = (): void => this.invalidationSubject.next();

  constructor(
    private readonly authService: AuthService,
    private readonly tenantContext: TenantContextService,
    private readonly socketService: SocketService,
    private readonly chatsService: CrmChatsService
  ) {
    this.socketService.on('connect', this.handleConnect);
    this.socketService.on(ChatEvents.CRM_CHAT_LIST_INVALIDATED, this.handleInvalidation);

    combineLatest([this.authService.currentUser$, this.tenantContext.selectedTenant$])
      .pipe(
        distinctUntilChanged(
          ([previousUser, previousTenant], [currentUser, currentTenant]) =>
            previousUser?._id === currentUser?._id &&
            previousUser?.role === currentUser?.role &&
            previousTenant?._id === currentTenant?._id
        ),
        tap(([user, selectedTenant]) => {
          this.contextEnabled =
            Boolean(user && CRM_CHAT_ROLES.has(user.role)) &&
            (user?.role !== RolesEnum.SUPER_ADMIN || Boolean(selectedTenant));

          if (!this.contextEnabled) {
            this.unreadChatsSubject.next(0);
            return;
          }
          this.subscribeToTenantRoom();
        }),
        switchMap(() => (this.contextEnabled ? this.loadCounter() : of(null)))
      )
      .subscribe();

    this.invalidationSubject
      .pipe(
        debounceTime(150),
        switchMap(() => this.loadCounter())
      )
      .subscribe();
  }

  refresh(): void {
    if (this.contextEnabled) {
      this.invalidationSubject.next();
    }
  }

  private loadCounter() {
    return this.chatsService.list({ scope: 'all', archive: 'active', page: 1, limit: 1 }).pipe(
      tap(response => this.unreadChatsSubject.next(response.counters.unread)),
      catchError(() => of(null))
    );
  }

  private subscribeToTenantRoom(): void {
    if (!this.contextEnabled) {
      return;
    }
    this.socketService.emit(ChatActions.CRM_SUBSCRIBE_CHAT_LIST, {
      clientRequestId: this.createRequestId(),
    });
  }

  private createRequestId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `crm-chat-list-${Date.now()}`;
  }
}
